// src/Dashboard/pages/user/News/utils/newsDashboardUtils.js

export const DEFAULT_VISIBLE_NEWS = 10;

export const DEFAULT_NEWS_UI = {
  search: "",
  showInactive: false,
  sort: "dateDesc", // dateDesc | dateAsc | alpha
  perPage: DEFAULT_VISIBLE_NEWS,
};

export const NEWS_UI_STORAGE_KEY = "news_dashboard_ui_state";

export const FALLBACK_NEWS_IMAGE = "/img/no-image.jpg";

export const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

export const stripHtml = (html = "") => {
  if (!html) return "";

  if (typeof document === "undefined") {
    return String(html)
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  return (div.textContent || div.innerText || "").trim();
};

export const strip = (value = "") =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const readStorageJson = (key, fallback) => {
  try {
    if (typeof window === "undefined") return fallback;

    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const writeStorageJson = (key, value) => {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
};

export const normalizePlainText = (value = "") => {
  return String(value || "").trim().replace(/\s+/g, " ");
};

export const isQuillEmpty = (html = "") => {
  const cleaned = String(html || "")
    .replace(/<p><br><\/p>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, "")
    .trim();

  return !cleaned;
};

export const normalizeRichText = (value = "") => {
  const html = String(value || "").trim();

  if (isQuillEmpty(html)) return "";

  return html;
};

export const getNewsDate = (news) => {
  return (
    news?.created_at ||
    news?.createdAt ||
    news?.updated_at ||
    news?.updatedAt ||
    null
  );
};

export const formatNewsDate = (date) => {
  if (!date) return "Date inconnue";

  try {
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(date));
  } catch {
    return "Date inconnue";
  }
};

export const getAuthorName = (news) => {
  const firstName = news?.author?.prenom || "";
  const lastName = news?.author?.nom || "";

  return `${firstName} ${lastName}`.trim();
};

export const getNewsSearchText = (news) => {
  return [
    news?.title,
    getAuthorName(news),
    stripHtml(news?.content || ""),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

export const sanitizeLight = (html = "") => {
  try {
    if (typeof DOMParser === "undefined") return "";

    const doc = new DOMParser().parseFromString(String(html || ""), "text/html");

    doc.querySelectorAll("script").forEach((node) => node.remove());

    doc.querySelectorAll("a").forEach((anchor) => {
      const href = anchor.getAttribute("href") || "";

      if (/^\s*javascript:/i.test(href)) {
        anchor.removeAttribute("href");
      }

      anchor.setAttribute("target", "_blank");
      anchor.setAttribute("rel", "noopener noreferrer");
    });

    return doc.body.innerHTML || "";
  } catch {
    return "";
  }
};

export const makeExcerptHtml = (html = "", maxChars = 320, maxBlocks = 4) => {
  const safe = sanitizeLight(html || "");

  if (!safe) return "";

  try {
    const doc = new DOMParser().parseFromString(safe, "text/html");
    const blocks = Array.from(
      doc.body.querySelectorAll("p,li,h2,h3,blockquote")
    );

    if (!blocks.length) return safe;

    let output = "";
    let total = 0;
    let used = 0;

    for (const element of blocks) {
      if (used >= maxBlocks) break;

      const text = (element.textContent || "").trim();

      if (!text) continue;

      const remaining = maxChars - total;

      if (remaining <= 0) break;

      let chunk = text;
      let cut = false;

      if (text.length > remaining) {
        chunk = text.slice(0, Math.max(0, remaining - 1)).trimEnd();
        cut = true;
      }

      output += `<p>${chunk}${cut ? "…" : ""}</p>`;
      total += chunk.length;
      used += 1;

      if (cut) break;
    }

    return output;
  } catch {
    return "";
  }
};

export const onImgError = (event) => {
  if (event?.currentTarget) {
    event.currentTarget.src = FALLBACK_NEWS_IMAGE;
  }
};

export const withBust = (url, version) => {
  if (!url) return url;

  return `${url}${url.includes("?") ? "&" : "?"}v=${version}`;
};